/**
 * DevicesTab -- Device list for the UserAdminPanel.
 *
 * Shows all WSD-tracked devices for the user with brand-specific icons,
 * accent-striped cards, connection count KPIs, IP chips, timestamp
 * hierarchy, and admin actions (rename + remove).
 */
import { useState, useCallback } from "react";
import { Badge } from "../../ui/Badge";
import { Button } from "../../ui/Button";
import { Icon } from "../../ui/Icon";
import { Input } from "../../ui/Input";
import { Spinner } from "../../ui/Spinner";
import { Switch } from "../../ui/Switch";
import { Tooltip } from "../../ui/Tooltip";
import { useDialog } from "../../ui/Dialog";
import { cn } from "../../../utils/cn";
import { detectPlatform, accentMap } from "../../../utils/devicePlatform";
import { toast } from "../../../lib/toast";
import { useDeviceRename, useDeviceDelete, useDeviceLock } from "../../../hooks/useStreamingDevices";
import type { WsdDevice } from "../../../hooks/useUserDevices";
import { formatRelativeTime, formatFullTimestamp } from "./helpers";

// ============================================================================
// Types
// ============================================================================

export type DevicesTabProps = {
  devices: WsdDevice[] | null;
  isLoading: boolean;
  error: string | null;
  serverType: string;
  username: string;
  onRefetch: () => void;
  enableAllDevices: boolean;
  settingsUpdating: boolean;
  onToggleAllDevices: (enabled: boolean) => Promise<void>;
};

// ============================================================================
// Sub-components
// ============================================================================

/** Small mono-styled chip for IP addresses */
const IpChip = ({ ip }: { ip: string }) => (
  <span className="inline-flex items-center gap-1 font-mono text-[10px] 2xl:text-xs bg-muted/30 text-muted-foreground px-1.5 py-0.5 rounded">
    <Icon icon="lucide:globe" className="h-2.5 w-2.5 opacity-50 shrink-0" />
    {ip}
  </span>
);

/** Small pill for platform or version metadata */
const MetaPill = ({
  icon,
  label,
}: {
  icon: string;
  label: string;
}) => (
  <span className="inline-flex items-center gap-1 text-[10px] 2xl:text-xs text-muted-foreground bg-muted/20 px-1.5 py-0.5 rounded">
    <Icon icon={icon} className="h-2.5 w-2.5 opacity-60 shrink-0" />
    <span className="truncate">{label}</span>
  </span>
);

/** Connection count KPI with tinted icon background */
const ConnectionKpi = ({
  count,
  accent,
}: {
  count: number;
  accent: keyof typeof accentMap;
}) => {
  const a = accentMap[accent];
  return (
    <div className="flex items-center gap-2">
      <div className={cn("rounded p-1 shrink-0", a.iconBg)}>
        <Icon icon="lucide:link" className={cn("h-3 w-3", a.iconColor)} />
      </div>
      <div className="min-w-0">
        <div className="text-sm 2xl:text-base font-bold text-foreground leading-tight tabular-nums">
          {count.toLocaleString()}
        </div>
        <div className="text-[9px] 2xl:text-[10px] text-muted-foreground leading-tight">
          connections
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// DeviceCard
// ============================================================================

type DeviceCardProps = {
  device: WsdDevice;
  username: string;
  isEditing: boolean;
  editValue: string;
  onEditValueChange: (val: string) => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
  onToggleLock: () => void;
  renameBusy: boolean;
  deleteBusy: boolean;
  lockBusy: boolean;
};

const DeviceCard = ({
  device,
  username,
  isEditing,
  editValue,
  onEditValueChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onToggleLock,
  renameBusy,
  deleteBusy,
  lockBusy,
}: DeviceCardProps) => {
  const displayName = device.custom_name ?? device.device_name;
  const isActive = device.is_active === 1;
  const platformInfo = detectPlatform(device.platform, device.device_name);
  const a = accentMap[isActive ? platformInfo.accent : "slate"];

  return (
    <div
      className={cn(
        "rounded-lg border border-border/50 bg-card",
        "border-l-2",
        isActive ? a.border : "border-l-border/40",
        "p-3 flex flex-col gap-2.5",
      )}
    >
      {/* Row 1: Icon + Name + Badge */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <div className={cn("rounded-md p-2 shrink-0", a.iconBg)}>
            <Icon
              icon={platformInfo.icon}
              className={cn("h-4 w-4 2xl:h-5 2xl:w-5", a.iconColor)}
            />
          </div>
          {isEditing ? (
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <Input
                value={editValue}
                onChange={(e) => onEditValueChange(e.target.value)}
                className="h-7 text-xs px-2 min-w-0 flex-1"
                autoFocus
                placeholder="Custom name..."
                onKeyDown={(e) => {
                  if (e.key === "Enter") onSaveEdit();
                  if (e.key === "Escape") onCancelEdit();
                }}
                disabled={renameBusy}
              />
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 shrink-0"
                onClick={onSaveEdit}
                disabled={renameBusy}
                aria-label="Save"
              >
                {renameBusy ? (
                  <Spinner size="3" />
                ) : (
                  <Icon icon="lucide:check" className="h-3.5 w-3.5 text-emerald-500" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 shrink-0"
                onClick={onCancelEdit}
                disabled={renameBusy}
                aria-label="Cancel"
              >
                <Icon icon="lucide:x" className="h-3.5 w-3.5 text-muted-foreground" />
              </Button>
            </div>
          ) : (
            <div className="min-w-0">
              <div className="text-xs 2xl:text-sm font-medium text-foreground truncate">
                {displayName}
              </div>
              {device.custom_name && (
                <div className="text-[10px] 2xl:text-xs text-muted-foreground truncate">
                  {device.device_name}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
          {device.isLocked && (
            <Tooltip content="Device is locked to this account">
              <Icon icon="lucide:lock" className="h-3 w-3 text-amber-500 shrink-0" />
            </Tooltip>
          )}
          <Badge
            variant={isActive ? "success" : "muted"}
            size="xs"
          >
            {isActive ? "Active" : "Inactive"}
          </Badge>
        </div>
      </div>

      {/* Row 2: Meta pills */}
      <div className="flex flex-wrap items-center gap-1.5">
        {device.platform && (
          <MetaPill icon={platformInfo.icon} label={device.platform} />
        )}
        {device.client_version && (
          <MetaPill icon="lucide:tag" label={`v${device.client_version}`} />
        )}
        {device.ip_address && <IpChip ip={device.ip_address} />}
      </div>

      {/* Row 3: Connection KPI + Timestamps */}
      <div className="flex items-end justify-between gap-3">
        <ConnectionKpi count={device.connection_count} accent={platformInfo.accent} />
        <div className="text-right shrink-0">
          <Tooltip content={formatFullTimestamp(device.last_seen)} side="top" align="end">
            <span className="text-xs 2xl:text-sm font-medium text-foreground tabular-nums cursor-default">
              {formatRelativeTime(device.last_seen)}
            </span>
          </Tooltip>
          <div className="text-[9px] 2xl:text-[10px] text-muted-foreground leading-tight mt-0.5">
            last seen
          </div>
          <Tooltip content={formatFullTimestamp(device.first_seen)} side="bottom" align="end">
            <span className="text-[10px] 2xl:text-xs text-muted-foreground tabular-nums cursor-default">
              since {formatRelativeTime(device.first_seen)}
            </span>
          </Tooltip>
        </div>
      </div>

      {/* Row 4: Admin actions */}
      <div className="flex items-center justify-end gap-1 border-t border-border/30 pt-2 mt-1">
        <Tooltip content={device.isLocked ? `Unlock ${displayName}` : `Lock to ${username}'s account`}>
          <button
            type="button"
            onClick={onToggleLock}
            disabled={lockBusy}
            className={cn(
              "inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors",
              "disabled:opacity-50 disabled:pointer-events-none",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70",
              device.isLocked
                ? "text-amber-500 hover:bg-amber-500/10"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            )}
          >
            <Icon icon={device.isLocked ? "lucide:lock" : "lucide:unlock"} className="h-3 w-3" />
          </button>
        </Tooltip>
        <Tooltip content="Rename device">
          <button
            type="button"
            onClick={onStartEdit}
            disabled={renameBusy}
            className="inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
          >
            <Icon icon="lucide:pencil" className="h-3 w-3" />
          </button>
        </Tooltip>
        <Tooltip content="Delete device from this dashboard">
          <button
            type="button"
            onClick={onDelete}
            disabled={deleteBusy}
            aria-label={`Delete ${displayName} from this dashboard`}
            className="inline-flex items-center justify-center h-7 w-7 rounded-md text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
          >
            <Icon icon="lucide:trash-2" className="h-3 w-3" />
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

// ============================================================================
// Component
// ============================================================================

export const DevicesTab = ({
  devices,
  isLoading,
  error,
  serverType,
  username,
  onRefetch,
  enableAllDevices,
  settingsUpdating,
  onToggleAllDevices,
}: DevicesTabProps) => {
  const [editingDeviceId, setEditingDeviceId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const { renaming, rename } = useDeviceRename();
  const { deleting, deleteDevice } = useDeviceDelete();
  const { locking, lockDevice } = useDeviceLock();
  const dialog = useDialog();

  const handleStartEdit = useCallback((deviceId: string, currentName: string) => {
    setEditingDeviceId(deviceId);
    setEditValue(currentName);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingDeviceId(null);
    setEditValue("");
  }, []);

  const handleSaveEdit = useCallback(
    async (device: WsdDevice) => {
      const trimmed = editValue.trim();
      const newName = trimmed === "" ? null : trimmed;
      try {
        await rename(serverType, username, device.device_id, newName, true);
        toast.success(newName ? "Device renamed" : "Custom name cleared");
        setEditingDeviceId(null);
        setEditValue("");
        onRefetch();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to rename device");
      }
    },
    [editValue, rename, serverType, username, onRefetch],
  );

  const handleDelete = useCallback(
    async (device: WsdDevice) => {
      const displayName = device.custom_name ?? device.device_name;
      const confirmed = await dialog.confirm({
        title: "Delete device?",
        description: `This permanently removes "${displayName}" from WSD tracking. It will only reappear if the device connects again.`,
        tone: "danger",
        confirmText: "Delete",
      });
      if (!confirmed) return;
      try {
        await deleteDevice(serverType, username, device.device_id);
        toast.success("Device deleted");
        onRefetch();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to remove device");
      }
    },
    [dialog, deleteDevice, serverType, username, onRefetch],
  );

  const handleToggleLock = useCallback(
    async (device: WsdDevice) => {
      const displayName = device.custom_name ?? device.device_name;
      const newLocked = !device.isLocked;
      const confirmed = await dialog.confirm({
        title: newLocked ? "Lock device?" : "Unlock device?",
        description: newLocked
          ? `This will bind "${displayName}" to ${username}'s account. The user will only be able to stream from their approved devices.`
          : `This will remove device restrictions for "${displayName}". The user will be able to stream from any device.`,
        tone: newLocked ? "warning" : "info",
        confirmText: newLocked ? "Lock" : "Unlock",
      });
      if (!confirmed) return;
      try {
        await lockDevice(serverType, username, device.device_id, newLocked);
        toast.success(newLocked ? "Device locked" : "Device unlocked");
        onRefetch();
      } catch {
        toast.error("Failed to update device lock");
      }
    },
    [dialog, lockDevice, serverType, username, onRefetch],
  );

  const handleToggleAllDevices = useCallback(
    async (enabled: boolean) => {
      if (!enabled) {
        // Toggling OFF = restricting to whitelist — confirm first
        const confirmed = await dialog.confirm({
          title: "Restrict to approved devices?",
          description: `This will limit ${username} to streaming only from their currently known devices. You can then lock/unlock individual devices below.`,
          tone: "warning",
          confirmText: "Restrict",
        });
        if (!confirmed) return;
      }
      try {
        await onToggleAllDevices(enabled);
        toast.success(enabled ? "All devices enabled" : "Switched to device whitelist");
        onRefetch();
      } catch {
        toast.error("Failed to update device access");
      }
    },
    [dialog, username, onToggleAllDevices, onRefetch],
  );

  // Loading
  if (isLoading && !devices) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Spinner size="4" className="mr-2" />
        <span className="text-xs 2xl:text-sm">Loading devices...</span>
      </div>
    );
  }

  // Error
  if (error && !devices) {
    return (
      <div className="rounded-md bg-destructive/10 border border-destructive/20 px-3 py-3">
        <div className="flex items-center gap-2">
          <Icon icon="lucide:alert-triangle" className="h-4 w-4 text-destructive shrink-0" />
          <div>
            <p className="text-xs 2xl:text-sm font-medium text-destructive-text">
              Failed to load devices
            </p>
            <p className="text-[10px] 2xl:text-xs text-destructive-text mt-0.5">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Empty
  if (!devices || devices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Icon icon="lucide:monitor-off" className="h-8 w-8 text-muted-foreground/30 mb-2" />
        <p className="text-xs 2xl:text-sm text-muted-foreground">No devices recorded</p>
        <p className="text-[10px] 2xl:text-xs text-muted-foreground mt-0.5">
          Devices appear after the user streams content
        </p>
      </div>
    );
  }

  // Sort by last_seen descending
  const sorted = [...devices].sort((a, b) => b.last_seen - a.last_seen);

  return (
    <div className="space-y-2">
      {/* All Devices toggle banner */}
      <div
        className={cn(
          "rounded-lg border px-3 py-2.5 flex items-center justify-between gap-3",
          enableAllDevices
            ? "bg-emerald-500/5 border-emerald-500/20"
            : "bg-amber-500/5 border-amber-500/20",
        )}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <Icon
            icon={enableAllDevices ? "lucide:monitor-check" : "lucide:shield-alert"}
            className={cn(
              "h-4 w-4 shrink-0",
              enableAllDevices ? "text-emerald-500" : "text-amber-500",
            )}
          />
          <div className="min-w-0">
            <div className="text-xs 2xl:text-sm font-medium text-foreground">
              {enableAllDevices ? "All Devices" : "Device Whitelist"}
            </div>
            <div className="text-[10px] 2xl:text-xs text-muted-foreground">
              {enableAllDevices
                ? "This user can stream from any device."
                : "Only approved devices can stream. Lock devices below to add them to the whitelist."}
            </div>
          </div>
        </div>
        <Switch
          checked={enableAllDevices}
          onChange={handleToggleAllDevices}
          disabled={settingsUpdating}
          aria-label="Toggle all devices access"
        />
      </div>

      <h4 className="text-[10px] 2xl:text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-1.5">
        Devices ({devices.length})
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {sorted.map((device) => (
          <DeviceCard
            key={device.id}
            device={device}
            username={username}
            isEditing={editingDeviceId === device.device_id}
            editValue={editingDeviceId === device.device_id ? editValue : ""}
            onEditValueChange={setEditValue}
            onStartEdit={() =>
              handleStartEdit(device.device_id, device.custom_name ?? device.device_name)
            }
            onSaveEdit={() => void handleSaveEdit(device)}
            onCancelEdit={handleCancelEdit}
            onDelete={() => void handleDelete(device)}
            onToggleLock={() => void handleToggleLock(device)}
            renameBusy={renaming}
            deleteBusy={deleting}
            lockBusy={locking}
          />
        ))}
      </div>
    </div>
  );
};
